import { ChevronDown, Monitor, Moon, Sun, UserCog, UserRound } from 'lucide-react';
import { Link } from 'react-router-dom';
import { ROTULO_PAPEL } from '@social/shared';
import { Dropdown } from '../componentes/dropdown';
import { useAparencia, type Tema } from '../lib/aparencia';
import { useSessao } from '../lib/sessao';

const TEMAS: { valor: Tema; rotulo: string; Icone: typeof Sun }[] = [
  { valor: 'claro', rotulo: 'Tema claro', Icone: Sun },
  { valor: 'escuro', rotulo: 'Tema escuro', Icone: Moon },
  { valor: 'automatico', rotulo: 'Seguir o sistema', Icone: Monitor },
];

/** Iniciais para o avatar: primeira e última palavra do texto. */
export function iniciaisDe(texto: string): string {
  const partes = texto.trim().split(/\s+/).filter(Boolean);
  if (partes.length === 0) return '';
  const primeira = partes[0]?.[0] ?? '';
  const ultima = partes.length > 1 ? (partes[partes.length - 1]?.[0] ?? '') : '';
  return (primeira + ultima).toUpperCase();
}

/**
 * Menu do usuário na barra do topo.
 *
 * Mostra nome, e-mail e o escritório — vindos de `useSessao().eu`, carregado
 * à parte por `GET /api/eu` (o token só carrega o papel). Enquanto `eu` não
 * chega, ou se a chamada falhar, tudo cai para `ROTULO_PAPEL[papel]`: entrar
 * e enfeitar são coisas diferentes, e a sessão não pode travar por isso.
 *
 * O "Sair" NÃO mora aqui — mora só na barra do topo (`Topbar`). Ele já
 * morou nos dois lugares ao mesmo tempo (o cenário de e2e clica em "Sair"
 * sem abrir menu nenhum, e por isso precisa do botão da barra), mas isso era
 * redundância sem ganho: não existe caso em que o da barra some e o deste
 * menu vire a única saída. Um só, na barra.
 */
export function MenuUsuario() {
  const papel = useSessao((s) => s.papel);
  const eu = useSessao((s) => s.eu);
  const tema = useAparencia((a) => a.tema);
  const definirTema = useAparencia((a) => a.definirTema);

  if (!papel) return null;

  const rotuloPapel = ROTULO_PAPEL[papel] ?? papel;
  const nomeExibido = eu?.nome ?? rotuloPapel;
  const iniciais = iniciaisDe(nomeExibido);

  return (
    <Dropdown
      rotulo="Abrir menu do usuário"
      classeGatilho="hover:bg-marca-100 flex items-center gap-2 rounded-full py-1 pr-1.5 pl-1 transition-colors"
      classePainel="w-64"
      gatilho={
        <>
          <span className="from-marca-600 to-marca-400 text-marca-contraste flex size-8 items-center justify-center rounded-full bg-gradient-to-br text-xs font-semibold">
            {iniciais || <UserRound className="size-4" aria-hidden />}
          </span>
          {/*
            `max-w-32` cortava "Contador responsável" em "Contador responsá…".
            A topbar tem espaço de sobra no desktop: 32 vira 48 em `lg` e 64 em
            `xl`. O `truncate` fica para o nome que realmente não couber.
          */}
          <span className="text-tinta-900 hidden max-w-40 truncate text-sm font-medium sm:inline lg:max-w-48 xl:max-w-64">
            {nomeExibido}
          </span>
          <ChevronDown className="text-tinta-500 hidden size-4 sm:inline" aria-hidden />
        </>
      }
    >
      <>
        <div className="flex items-center gap-3 px-3 pt-3 pb-3">
          <span className="from-marca-600 to-marca-400 ring-marca-600/20 text-marca-contraste flex size-11 shrink-0 items-center justify-center rounded-full bg-gradient-to-br text-sm font-semibold ring-2">
            {iniciais || <UserRound className="size-5" aria-hidden />}
          </span>
          <div className="min-w-0 flex-1">
            <p className="text-tinta-900 truncate text-sm leading-tight font-semibold">
              {nomeExibido}
            </p>
            {eu && (
              <>
                <p className="text-tinta-500 truncate text-xs">{eu.email}</p>
                <p className="text-tinta-500 truncate text-xs">{eu.escritorio.nome}</p>
              </>
            )}
          </div>
        </div>

        {/*
          A porta da tela de conta. Fica AQUI, e não no menu lateral, porque é
          onde a pessoa procura os próprios dados — o avatar com o nome dela.
          No lateral seria mais um item de navegação de trabalho.
        */}
        <div className="border-tinta-100 mt-1 border-t px-1 pt-1">
          <Link
            to="/conta"
            className="text-tinta-900 hover:bg-marca-100 focus-visible:ring-marca-400 flex items-center gap-2 rounded-md px-2 py-2 text-sm transition-colors focus-visible:ring-2 focus-visible:outline-none"
          >
            <UserCog className="text-tinta-500 size-4" aria-hidden />
            Minha conta
          </Link>
        </div>

        <div className="border-tinta-100 mt-1 flex items-center justify-between border-t px-3 py-2.5">
          <span className="text-tinta-900 text-sm">Tema</span>
          <div className="bg-tinta-100 inline-flex rounded-md p-0.5">
            {TEMAS.map(({ valor, rotulo, Icone }) => (
              <button
                key={valor}
                type="button"
                onClick={() => definirTema(valor)}
                aria-pressed={tema === valor}
                aria-label={rotulo}
                title={rotulo}
                className={`flex h-7 w-9 items-center justify-center rounded transition-colors ${
                  tema === valor
                    ? 'bg-superficie text-tinta-900 shadow-sm'
                    : 'text-tinta-500 hover:text-marca-600'
                }`}
              >
                <Icone className="size-4" aria-hidden />
              </button>
            ))}
          </div>
        </div>
      </>
    </Dropdown>
  );
}
